import React, { FunctionComponent, useState, useEffect, useCallback } from "react"
import { Link, useParams } from "react-router"
import { Button, Modal, notification, Row, Col, Table, Tabs, Tooltip } from "antd"
import { TraderMeta } from "../components/trader/meta"
import { TradersService, PositionsService, DecisionsService } from "../api/services.gen"
import { TablePaginationConfig } from "antd/lib/table/interface"
import { ModalFuncProps } from "antd/lib/modal/interface"
import { PaginationProps } from "antd/lib/pagination/Pagination"
import { DecisionDto, PositionDto, TraderDto } from "../api"

const DEFAULT_COUNT = 20

type TraderParams = { id: string }

interface TraderProps {
  trader: TraderDto
}

const TraderPage:FunctionComponent<{}> = (props) => {
  const params = useParams<TraderParams>()
  const [trader, setTrader] = useState<TraderDto | null>(null)

  const [loadingPositions, setLoadingPositions] = useState<boolean>(false)
  const [positions, setPositions] = useState<PositionDto[]>([])
  const [positionsPagination, setPositionsPagination] = useState<PaginationProps>({ pageSize: DEFAULT_COUNT })

  const [loadingDecisions, setLoadingDecisions] = useState<boolean>(false)
  const [decisions, setDecisions] = useState<DecisionDto[]>([])
  const [decisionsPagination, setDecisionsPagination] = useState<PaginationProps>({ pageSize: DEFAULT_COUNT })

  const [modal, contextHolder] = Modal.useModal();

  const modalDelete: ModalFuncProps = {
    title: 'Are you sure?',
    content: 'This will leave current positions as it is. You must close it manually.',
    onOk: async () => {
      try {
        await TradersService.deleteTrader({ traderId: trader!.id })
        notification.success({
          message: 'Trader deleted',
          description: 'Actions opened are still open, you must close it manually'
        })
      } catch (e: any) {
        console.error(e)
        notification.error({
          message: 'Error',
          description: e.toString()
        })
      }
    }
  }

  const modalSellProfitable: ModalFuncProps = {
    title: 'Are you sure?',
    content: 'This will sell all current profitable positions. Is this emotionnal?',
    onOk: async () => {
      try {
        const res = await TradersService.sellProfitableTrader({ traderId: trader!.id })
        notification.success({
          message: 'Profitable positions sold',
          description: `Trader sold ${res.sold} positions`
        })
      } catch (e: any) {
        console.error(e)
        notification.error({
          message: 'Error',
          description: e.toString()
        })
      }
    }
  }

  const positionsColumns = [
    {
      title: 'Action',
      dataIndex: 'action',
      key: 'action',
    },
    {
      title: 'Open date',
      dataIndex: 'openDate',
      key: 'openDate',
    },
    {
      title: 'Quantity',
      dataIndex: 'quantity',
      key: 'quantity',
      sorter: (a: PositionDto, b: PositionDto) => a.quantity - b.quantity
    },
    {
      title: 'Open price',
      dataIndex: 'openPrice',
      key: 'openPrice',
      sorter: (a: PositionDto, b: PositionDto) => a.openPrice - b.openPrice
    },
    {
      title: 'Close date',
      dataIndex: 'closeDate',
      key: 'closeDate',
    },
    {
      title: 'Close price',
      dataIndex: 'closePrice',
      key: 'closePrice',
      sorter: (a: PositionDto, b: PositionDto) => {
        if (!a.closePrice) {
          return -Infinity
        }
        if (!b.closePrice) {
          return +Infinity
        }
        return a.closePrice - b.closePrice
      },
    },
    {
      title: 'Profit',
      dataIndex: 'profit',
      sorter: (a: PositionDto, b: PositionDto) => {
        if (!a.profit) {
          return -Infinity
        }
        if (!b.profit) {
          return +Infinity
        }
        return a.profit - b.profit
      },
      render: (profit: number, position: PositionDto) => {
        if (profit === 0) {
          return ''
        }
        return profit.toFixed(4)
      }
    },
  ]
  const decisionsColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date'
    },
    {
      title: 'In',
      dataIndex: 'type',
      key: 'type'
    },
    {
      title: 'Wanted to',
      dataIndex: 'wantedTo',
      key: 'wantedTo'
    },
    {
      title: 'Out',
      dataIndex: 'result',
      key: 'result',
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason'
    },
    {
      title: 'Meta',
      dataIndex: 'meta',
      key: 'meta',
      render: (e: any) => e ? <>{Object.keys(e).map(key => <>{key}: {e[key].toString()}<br /></>)}</> : null
    }
  ]

  const fetchData = useCallback(async () => {
    const response = await TradersService.getTrader({ traderId: params.id! })
    setTrader(response.trader)
  }, [params.id])
  const fetchPositions = useCallback(async (skip: number = 0, take: number = DEFAULT_COUNT) => {
    setLoadingPositions(true)
    const response = await PositionsService.getPositionsv2({ traderId: params.id!, take: DEFAULT_COUNT, skip })
    setPositions(response.positions)
    setPositionsPagination({
      ...positionsPagination,
      pageSize: take,
      total: response.count,
    })
    setLoadingPositions(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id])
  const fetchDecisions = useCallback(async (skip: number = 0, take: number = DEFAULT_COUNT) => {
    setLoadingDecisions(true)
    const response = await DecisionsService.getDecisions({ traderId: params.id, take, skip })
    setDecisions(response.decisions.sort((a: DecisionDto, b: DecisionDto) => new Date(b.date).getTime() - new Date(a.date).getTime()))
    setDecisionsPagination({
      ...decisionsPagination,
      pageSize: take,
      total: response.count,
    })
    setLoadingDecisions(false)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id])

  useEffect(() => {
    fetchData()
    fetchPositions()
    fetchDecisions()
  }, [fetchData, fetchDecisions, fetchPositions, params.id])

  return <>
    { trader ? <TraderMeta trader={trader} full={true} /> : <p>Loading...</p> }<br />
    <Link to={`/traders/trader/${trader?.id}/edit`}><Button type="primary">Edit trader</Button></Link>
    {trader?.instrument
      ? <Link to={`/instruments/instrument/${trader.instrument.code}`}><Button>See instrument</Button></Link>
      : <Tooltip title="No instrument linked to this trader"><span><Button disabled>See instrument</Button></span></Tooltip>
    }<br />
    <Link to={`/traders/trader/${trader?.id}/addPosition`}><Button>Manual add position</Button></Link>
    <Button>Force buy {trader?.quantity}</Button>
    <Button>Force sell all</Button>
    <Button onClick={() => modal.confirm(modalSellProfitable)}>Force sell profitable</Button><br />
    <Button color="danger" variant='solid' onClick={() => modal.confirm(modalDelete)}>Delete</Button>
    <br /><br />

    <Tabs defaultActiveKey="1" style={{ fontFeatureSettings: "'ss01' 1, 'tnum' 1" }}>
      <Tabs.TabPane tab="Positions" key="1">
        <Table dataSource={positions} columns={positionsColumns} loading={loadingPositions} pagination={positionsPagination} onChange={(pagination: PaginationProps) => {
          fetchPositions((((pagination.current) ?? 1) - 1) * DEFAULT_COUNT, pagination.pageSize)
        }} />
      </Tabs.TabPane>
      <Tabs.TabPane tab="Decisions" key="2">
        <Table dataSource={decisions} columns={decisionsColumns} loading={loadingDecisions} pagination={decisionsPagination} onChange={(pagination: PaginationProps) => {
          fetchDecisions((((pagination.current) ?? 1) - 1) * DEFAULT_COUNT, pagination.pageSize)
        }} />
      </Tabs.TabPane>
    </Tabs>
    

    {contextHolder}
  </>
}

export { TraderPage }